import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Card,
  Layout,
  Text,
  Badge,
  Button,
  Icon,
  BlockStack,
  InlineStack,
  TextContainer,
  TextField,
  ButtonGroup,
  Tooltip,
  Spinner,
} from '@shopify/polaris';
import {
  CircleTickMajor,
  ContentMinor,
  EditMajor,
  DeleteMajor,
  DuplicateMinor,
  AddMajor,
  ViewMajor,
} from '@shopify/polaris-icons';
import '@shopify/polaris/build/esm/styles.css';
import '../modal-overrides.css';
import '../dashboard.css';

// Import services
import { storiesService } from '../services/storiesService';

// Import modal components
import StoryDetailsModal from './modals/StoryDetailsModal';
import CreateStoryModal from './modals/CreateStoryModal';
import EditStoryModal from './modals/EditStoryModal';
import DeleteStoryModal from './modals/DeleteStoryModal';
import DuplicateStoryModal from './modals/DuplicateStoryModal';
import RestoreStoryModal from './modals/RestoreStoryModal';
import StoryPreviewModal from './modals/StoryPreviewModal';
import AuthenticatedAssetImage from './AuthenticatedAssetImage';
import PreviewImageModal from './modals/PreviewImageModal';

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

const PreviewerStoriesPage = ({ setToastMessage, setToastActive, isCreateModalOpen, setIsCreateModalOpen }) => {

    const [currentTab, setCurrentTab] = useState('all');
    const [stories, setStories] = useState([]);
    const [deletedStories, setDeletedStories] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [isLoadingStories, setIsLoadingStories] = useState(false);
    const [isLoadingDeletedStories, setIsLoadingDeletedStories] = useState(false);
    // Add state for Story Preview modal
    const [isStoryPreviewModalOpen, setIsStoryPreviewModalOpen] = useState(false);
    const [selectedStoryForPreview, setSelectedStoryForPreview] = useState(null);
    const [selectedStory, setSelectedStory] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeletingStory, setIsDeletingStory] = useState(false);
    const [deleteStoryId, setDeleteStoryId] = useState('');
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [duplicateStoryId, setDuplicateStoryId] = useState('');
    const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
    const [isRestoringStory, setIsRestoringStory] = useState(false);
    const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
    const [restoreStoryId, setRestoreStoryId] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    // Add state for Image Preview modal
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);
    const [selectedImageUrl, setSelectedImageUrl] = useState('');
    const [selectedImageAlt, setSelectedImageAlt] = useState('');

    // Table headings
    const headings = [
      'Image',
      'Story',
      'Slug',
      'Gender',
      'Age',
      'Language',
      'Actions',
    ];

    // Stories functions
    const fetchStories = useCallback(async () => {
        setIsLoadingStories(true);
        try {
        const data = await storiesService.getAllStories();
        const storiesArray = Array.isArray(data) ? data : [];
        setStories(storiesArray);
        } catch (err) {
        console.error('Fetch error:', err);
        setStories([]); // Ensure stories is always an array
        } finally {
        setIsLoadingStories(false);
        }
    }, []);

    // Deleted stories functions
    const fetchDeletedStories = useCallback(async () => {
        setIsLoadingDeletedStories(true);
        try {
          const data = await storiesService.getDeletedStories();
          const deletedStoriesArray = Array.isArray(data) ? data : [];
          setDeletedStories(deletedStoriesArray);
        } catch (err) {
          console.error('Fetch deleted stories error:', err);
          setDeletedStories([]); // Ensure deletedStories is always an array
        } finally {
          setIsLoadingDeletedStories(false);
        }
    }, []);

    // Add handler for Story Preview
    const handleStoryPreview = useCallback((story) => {
        setSelectedStoryForPreview(story);
        setIsStoryPreviewModalOpen(true);
    }, []);

    // Add handler for Image Preview
    const handleImagePreview = useCallback((imageUrl, imageAlt) => {
        setSelectedImageUrl(imageUrl);
        setSelectedImageAlt(imageAlt);
        setIsImageModalOpen(true);
    }, []);

    // Handle edit story
    const handleEditStory = useCallback((story) => {
        setSelectedStory(story);
        setIsEditModalOpen(true);
    }, []);

    const handleDeleteStory = useCallback((id) => {
        console.log('Delete story called with ID:', id);
        console.log('Story ID type:', typeof id);
        console.log('Setting deleteStoryId to:', id);
        console.log('Setting isDeleteModalOpen to true');
        setDeleteStoryId(id);
        setIsDeleteModalOpen(true);
        console.log('Delete modal should now be open');
    }, []);

    const handleConfirmDelete = useCallback(async () => {
        if (!deleteStoryId) return;

        console.log('Confirming delete for story ID:', deleteStoryId);
        console.log('Delete story ID type:', typeof deleteStoryId);

        setIsDeletingStory(true);
        try {
        await storiesService.deleteStory(deleteStoryId);
        setToastMessage('Story Deleted Successfully');
        setToastActive(true);
        setTimeout(() => {
            setToastActive(false);
        }, 3000);
        fetchStories();
        setIsDeleteModalOpen(false);
        setDeleteStoryId('');
        } catch (err) {
        console.error('Error deleting story:', err);
        setToastMessage(`Error: ${err.message || 'Failed to delete story'}`);
        setToastActive(true);
        setTimeout(() => {
            setToastActive(false);
        }, 5000);
        } finally {
        setIsDeletingStory(false);
        }
    }, [deleteStoryId, fetchStories]);

    const handleDuplicateStory = useCallback((id) => {
        setDuplicateStoryId(id);
        setIsDuplicateModalOpen(true);
    }, []);

    const handleConfirmDuplicate = useCallback(async () => {
        if (!duplicateStoryId) return;

        try {
        const story = (stories || []).find(s => s._id === duplicateStoryId);
        if (!story) {
            throw new Error('Story not found');
        }

        // Generate duplicate slug and title with timestamp
        const timestamp = Date.now();
        const randomSuffix = Math.random().toString(36).substring(2, 8); // 6 character random string
        let duplicateSlug = `${story.slug}-copy-${timestamp}-${randomSuffix}`;
        let duplicateTitle = `${story.title} Copy`;
        let counter = 2;

        // Check if slug already exists and increment counter (though unlikely with timestamp)
        const existingSlugs = stories.map(s => s.slug);
        while (existingSlugs.includes(duplicateSlug)) {
            duplicateSlug = `${story.slug}-copy-${timestamp}-${randomSuffix}-${counter}`;
            duplicateTitle = `${story.title} Copy ${counter}`;
            counter++;
        }

        console.log('Duplicating story with data:', {
            slug: duplicateSlug,
            title: duplicateTitle
        });

        await storiesService.duplicateStory(duplicateStoryId, {
            slug: duplicateSlug,
            title: duplicateTitle
        });
        setToastMessage('Story Duplicated Successfully');
        setToastActive(true);
        setTimeout(() => {
            setToastActive(false);
        }, 3000);
        fetchStories();
        setIsDuplicateModalOpen(false);
        setDuplicateStoryId('');
        } catch (err) {
        console.error('Error duplicating story:', err);
        setToastMessage(`Error: ${err.message || 'Failed to duplicate story'}`);
        setToastActive(true);
        setTimeout(() => {
            setToastActive(false);
        }, 5000);
        }
    }, [duplicateStoryId, stories, fetchStories]);

    const handleRestoreStory = useCallback((id) => {
        setRestoreStoryId(id);
        setIsRestoreModalOpen(true);
    }, []);

    const handleConfirmRestore = useCallback(async () => {
        if (!restoreStoryId) return;

        setIsRestoringStory(true);
        try {
        await storiesService.restoreStory(restoreStoryId);
        setToastMessage('Story Restored Successfully');
        setToastActive(true);
        setTimeout(() => {
            setToastActive(false);
        }, 3000);
        fetchDeletedStories(); // Refresh deleted stories list
        if (currentTab === 'all') {
            fetchStories(); // Refresh all stories list
        }
        setIsRestoreModalOpen(false);
        setRestoreStoryId('');
        } catch (err) {
        console.error('Error restoring story:', err);
        setToastMessage(`Error: ${err.message || 'Failed to restore story'}`);
        setToastActive(true);
        setTimeout(() => {
            setToastActive(false);
        }, 5000);
        } finally {
        setIsRestoringStory(false);
        }
    }, [restoreStoryId, currentTab, fetchStories, fetchDeletedStories]);



    useEffect(() => {
        if (currentTab === 'all') {
            fetchStories();
          } else if (currentTab === 'deleted') {
            fetchDeletedStories();
          }
      }, [currentTab, fetchStories, fetchDeletedStories]);
    
      useEffect(() => {
        fetchStories();
      }, [fetchStories]);

    // Render each story as a row
    return (
      <Layout style={{ marginBottom: '24px' }}>


        <Layout.Section>
          <Card>
            {/* Filter/Status Tabs and Search Bar Row */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 16px 0 16px' }}>
              {/* Tabs */}
              <div style={{ display: 'flex', gap: 8 }}>
                <ButtonGroup>
                  <Button 
                    onClick={() => setCurrentTab('all')}
                    variant="secondary"
                    tone={currentTab === 'all' ? 'success' : undefined}
                  >
                    All
                  </Button>
                  <Button 
                    onClick={() => setCurrentTab('deleted')}
                    variant="secondary"
                    tone={currentTab === 'deleted' ? 'success' : undefined}
                  >
                    Deleted
                  </Button>
                </ButtonGroup>
              </div>
              {/* Search Bar */}
              {(currentTab === 'all' ? stories : deletedStories).length > 0 && (
                <div style={{ minWidth: 260 }}>
                  <TextField
                    label="Search by title or description..."
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder="Search by title or description..."
                    autoComplete="off"
                    prefix={
                      <span className="Polaris-Icon">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="9" cy="9" r="7.5" stroke="var(--s-icon-color-25041, rgba(74, 74, 74, 1))" strokeWidth="1.5" />
                          <path d="M15 15L18 18" stroke="var(--s-icon-color-25041, rgba(74, 74, 74, 1))" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                      </span>
                    }
                    labelHidden
                  />
                </div>
              )}
            </div>
            <div style={{ overflowX: 'auto', marginTop: 16 }}>
              <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                {(currentTab === 'all' ? stories : deletedStories).filter(story => {
                  const q = searchQuery.toLowerCase();
                  return (
                    (story.title && story.title.toLowerCase().includes(q)) ||
                    (story.description && story.description.toLowerCase().includes(q)) ||
                    (story.language && story.language.toLowerCase().includes(q)) ||
                    (story.age && story.age.toLowerCase().includes(q)) ||
                    (story.gender && story.gender.toLowerCase().includes(q))
                  );
                }).length > 0 && (
                  <thead>
                    <tr>
                      {headings.map((heading, idx) => (
                        <th key={idx} style={{
                          textAlign: 'center',
                          padding: '12px 8px',
                          fontWeight: 600,
                          color: '#6d7175',
                          fontSize: 13,
                          background: '#fafbfc',
                          borderBottom: '1px solid #e3e3e3',
                          minWidth: idx === 0 ? 56 : 80,
                        }}>{heading}</th>
                      ))}
                    </tr>
                  </thead>
                )}
                <tbody>
                  {isLoadingStories || isLoadingDeletedStories ? (
                    <tr>
                      <td colSpan="7" style={{ padding: '60px 16px', textAlign: 'center' }}>
                        <BlockStack gap="400" align="center">
                          <Spinner size="large" />
                          <Text variant="bodyMd" tone="subdued">Loading stories...</Text>
                        </BlockStack>
                      </td>
                    </tr>
                  ) : (currentTab === 'all' ? stories : deletedStories).filter(story => {
                    const q = searchQuery.toLowerCase();
                    return (
                      (story.title && story.title.toLowerCase().includes(q)) ||
                      (story.description && story.description.toLowerCase().includes(q)) ||
                      (story.language && story.language.toLowerCase().includes(q)) ||
                      (story.age && story.age.toLowerCase().includes(q)) ||
                      (story.gender && story.gender.toLowerCase().includes(q))
                    );
                  }).length > 0 ? (
                    (currentTab === 'all' ? stories : deletedStories).filter(story => {
                      const q = searchQuery.toLowerCase();
                      return (
                        (story.title && story.title.toLowerCase().includes(q)) ||
                        (story.description && story.description.toLowerCase().includes(q)) ||
                        (story.language && story.language.toLowerCase().includes(q)) ||
                        (story.age && story.age.toLowerCase().includes(q)) ||
                        (story.gender && story.gender.toLowerCase().includes(q))
                      );
                    }).map(story => {
                      const imageUrl = (story.firstScene && story.firstScene.scene_url) || story.image || 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-image.png';
                      console.log('Story object:', story);
                      console.log('Story _id:', story._id);
                      return (
                        <tr key={story._id || story.slug} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '12px 8px', textAlign: 'center' }}>{/* Image */}
                            <div 
                              style={{ 
                                width: 48, 
                                height: 48, 
                                borderRadius: 8, 
                                overflow: 'hidden', 
                                background: '#f6f6f7', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center', 
                                margin: '0 auto',
                                cursor: 'pointer',
                                transition: 'transform 0.2s ease-in-out'
                              }}
                              onClick={() => handleImagePreview(imageUrl, story.title)}
                              onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                            >
                              <AuthenticatedAssetImage
                                src={imageUrl}
                                assetSize="thumbnail"
                                alt={story.title}
                                style={{ width: 48, height: 48, objectFit: 'cover' }}
                              />
                            </div>
                          </td>
                          <td style={{ padding: '12px 8px', minWidth: 180, textAlign: 'center' }}>
                            <div>
                              <Text variant="bodyMd">{story.title}</Text>
                              <Text variant="bodyMd" tone="subdued" style={{
                                display: '-webkit-box',
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                lineHeight: '1.4',
                                fontSize: '13px',
                                marginTop: '4px'
                              }}>
                                {story.description && story.description.length > 50 
                                  ? `${story.description.substring(0, 50)}...` 
                                  : story.description}
                              </Text>
                            </div>
                          </td>
                          <td style={{ padding: '12px 8px', minWidth: 120, color: '#6d7175', fontSize: 13, textAlign: 'center' }}>{story.slug}</td>
                          <td style={{ padding: '12px 8px', minWidth: 100, color: '#6d7175', fontSize: 13, textAlign: 'center' }}>{story.gender && story.gender.trim() ? story.gender : ''}</td>
                          <td style={{ padding: '12px 8px', minWidth: 100, color: '#6d7175', fontSize: 13, textAlign: 'center' }}>{story.age && story.age.trim() ? story.age : ''}</td>
                          <td style={{ padding: '12px 8px', minWidth: 100, color: '#6d7175', fontSize: 13, textAlign: 'center' }}>{story.language && story.language.trim() ? story.language : ''}</td>
                          <td style={{ padding: '12px 8px', minWidth: 120, textAlign: 'center' }}>
                            <InlineStack gap="100" align="center">
                              {currentTab === 'all' ? (
                                <>
                                  <Tooltip content="Preview Scenes">
                                    <Button 
                                      icon={ViewMajor} 
                                      size="slim" 
                                      onClick={() => handleStoryPreview(story)}
                                    />
                                  </Tooltip>
                                  <Tooltip content="Edit">
                                    <Button 
                                      icon={EditMajor} 
                                      size="slim" 
                                      onClick={() => handleEditStory(story)}
                                    />
                                  </Tooltip>
                                  <Tooltip content="Delete">
                                    <Button 
                                      icon={DeleteMajor} 
                                      size="slim" 
                                      onClick={() => {
                                        console.log('Delete button clicked!');
                                        console.log('Story object:', story);
                                        console.log('Story _id:', story._id);
                                        handleDeleteStory(story._id);
                                      }}
                                    />
                                  </Tooltip>
                                  <Tooltip content="Duplicate">
                                    <Button 
                                      icon={DuplicateMinor} 
                                      size="slim" 
                                      onClick={() => handleDuplicateStory(story._id)}
                                    />
                                  </Tooltip>
                                </>
                              ) : (
                                <Tooltip content="Restore">
                                  <Button 
                                    icon={CircleTickMajor} 
                                    size="slim" 
                                    onClick={() => handleRestoreStory(story._id)}
                                  />
                                </Tooltip>
                              )}
                            </InlineStack>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="7" style={{ padding: '60px 16px', textAlign: 'center' }}>
                        <BlockStack gap="400" align="center">
                          <Icon 
                            source={ContentMinor} 
                            color="subdued" 
                            className="empty-state-big-icon"
                          />
                          <Text variant="bodyMd" tone="subdued">No records found</Text>
                          {currentTab === 'all' && (
                            <div style={{ display: 'inline-block' }}>
                              <Button 
                                icon={AddMajor} 
                                onClick={() => setIsCreateModalOpen(true)}
                                variant="primary"
                                size="large"
                              >
                                Add Story
                              </Button>
                            </div>
                          )}
                        </BlockStack>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </Layout.Section>

        {/* Story Details Modal */}
        <StoryDetailsModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          selectedStory={selectedStory}
        />

        {/* Create Story Modal */}
        <CreateStoryModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          setToastMessage={setToastMessage}
          setToastActive={setToastActive}
          onStoryCreated={fetchStories}
        />

        {/* Edit Story Modal */}
        <EditStoryModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          selectedStory={selectedStory}
          setToastMessage={setToastMessage}
          setToastActive={setToastActive}
          onStoryUpdated={fetchStories}
        />

        {/* Delete Confirmation Modal */}
        <DeleteStoryModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={handleConfirmDelete}
          isDeleting={isDeletingStory}
        />

        {/* Duplicate Confirmation Modal */}
        <DuplicateStoryModal
          isOpen={isDuplicateModalOpen}
          onClose={() => setIsDuplicateModalOpen(false)}
          onConfirm={handleConfirmDuplicate}
        />

        {/* Restore Confirmation Modal */}
        <RestoreStoryModal
          isOpen={isRestoreModalOpen}
          onClose={() => setIsRestoreModalOpen(false)}
          onConfirm={handleConfirmRestore}
          isRestoring={isRestoringStory}
        />

        {/* Story Preview Modal */}
        <StoryPreviewModal
          isOpen={isStoryPreviewModalOpen}
          onClose={() => setIsStoryPreviewModalOpen(false)}
          selectedStory={selectedStoryForPreview}
          setToastMessage={setToastMessage}
          setToastActive={setToastActive}
        />

        {/* Image Preview Modal */}
        <PreviewImageModal
          isOpen={isImageModalOpen}
          onClose={() => setIsImageModalOpen(false)}
          imageUrl={selectedImageUrl}
          imageAlt={selectedImageAlt}
        />
      </Layout>
    );
  };

export default PreviewerStoriesPage;